// A free replay's register page, /register/<slug>?src=<source> (docs/replay-register-spec.md, Adam's
// cut-down version of 23 Oct): the calls to replay-register and the pure rules the page and admin share.
// Nothing here imports the app's settings or React, so tests/e2e can check it in Node; the screen
// (screens/Register.jsx) passes the Supabase address and the publishable key in.
//
// - replay-register is 1 of the 4 functions that answer without a login (decision 57). It's called the
//   way the login screen calls request-code: a POST with JSON and the publishable key on apikey only,
//   never a member's session, so the page answers the same logged in or out.
// - The first name and email go in the POST's body only, never in an address, and nothing here writes
//   them anywhere else. The page keeps only the pass, per slug, in this browser.
// - Every answer is { ok: true, ... } or { ok: false, error } with error 1 of ERRORS. No answer, or 1
//   that can't be read, is 'failed'; a 429 with no body is 'busy'.
// - The pass plays only that replay (spec 2). It's kept in localStorage per slug, every read and write
//   in try/catch, so the page works the same with storage off: they just see the form again.
import * as f from '../lib/format.js'
import { parseVimeo } from './vimeo.js'

// The live hub's own address, for the full link admin shows and copies
export const REGISTER_ORIGIN = 'https://learn.adamslee.co.uk'
export const REGISTER_BASE = '/register/'

// The slug's shape, as hub.webinars' check has it: lower-case letters and numbers in groups joined by
// single hyphens, 3 to 60 characters
export const SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/
export const isSlug = s => typeof s === 'string' && s.length >= 3 && s.length <= 60 && SLUG_SHAPE.test(s)

export const registerPath = slug => `${REGISTER_BASE}${slug}`
export const registerLink = slug => `${REGISTER_ORIGIN}${registerPath(slug)}`

// The slug a path names, or null: '/register/4-things-october' (or with a trailing slash, or in
// capitals, as a link typed by hand can be) gives '4-things-october'
export function registerSlugIn(path) {
  if (!path || !path.startsWith(REGISTER_BASE)) return null
  const rest = path.slice(REGISTER_BASE.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  let slug
  try { slug = decodeURIComponent(rest).toLowerCase() } catch { return null }
  return isSlug(slug) ? slug : null
}

// ---- What they type --------------------------------------------------------------------------------

// greeting.ts's plainFirstName rule, which the function checks again: a letter, then letters, an
// apostrophe or a hyphen, up to 60 characters
const PLAIN_NAME = /^\p{L}[\p{L}\p{M}'’-]{0,59}$/u
// hub.people's email check, as request-code and the login screen have it
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// The 11 titles private.first_name_from passes over (20261010203000), in any case, with or without dots
// after, as the function's isTitleWord has them
const TITLES = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'sir', 'rev', 'lady', 'lord'])
export const isTitleWord = word => TITLES.has(String(word).replace(/\.+$/u, '').toLowerCase())

// The first name as the page sends it and the function takes it (Adam, 23 Oct): the first word of what's
// typed, once trimmed, so "Mary Ann", or a full name typed from an ad, gives "Mary", with any titles before
// it passed over, so "Mr John Smith" gives "John"; a title on its own gives nothing
export const firstWordOf = value => String(value ?? '').trim().split(/\s+/u).find(w => w && !isTitleWord(w)) || ''

export function firstNameProblem(value) {
  if (!String(value ?? '').trim()) return 'empty'
  const n = firstWordOf(value)
  return n && PLAIN_NAME.test(n) ? null : 'shape'
}

export function emailProblem(value) {
  const e = String(value ?? '').trim()
  if (!e) return 'empty'
  return e.length <= 254 && EMAIL.test(e) ? null : 'shape'
}

// The words the page shows (Adam's writing rules: British English, no exclamation marks, no dashes)
export const WORDS = {
  eyebrow: 'Free training replay',
  line: 'Free training replay. Register and watch now.',
  firstName: 'First name',
  email: 'Email',
  button: 'Watch the replay',
  smallPrint: "You'll get emails from me and can unsubscribe at any time.",
  privacy: 'Privacy policy',
  gone: "This replay isn't available at the moment.",
  firstNameEmpty: 'Type your first name.',
  firstNameShape: 'Type your first name in letters, with no numbers.',
  emailEmpty: 'Type your email address.',
  emailShape: "That doesn't look like an email address. Check it and try again.",
  busy: 'There have been too many tries just now. Wait a few minutes and try again.',
  failed: "That didn't go through. Check your connection and try again.",
  loadFailed: "That didn't load. Check your connection and try again.",
  tryAgain: 'Try again',
  replay: 'Replay',
  chapters: 'Chapters',
  // the honeypot's label, which only a bot reads (it's off the screen and hidden from screen readers)
  website: 'Website',
}

export function fieldWords(field, problem) {
  if (!problem) return null
  if (field === 'first_name') return problem === 'empty' ? WORDS.firstNameEmpty : WORDS.firstNameShape
  return problem === 'empty' ? WORDS.emailEmpty : WORDS.emailShape
}

// ---- The calls -------------------------------------------------------------------------------------

export const ERRORS = ['first_name', 'email', 'gone', 'busy', 'expired', 'failed']

// 1 call to replay-register: { action, ...body }. Answers its { ok: true, ... }, or { ok: false, error }.
// conn: { url, key } (the Supabase address and the publishable key) and, for a test, fetchImpl.
export async function callReplayRegister(action, body, { url, key, fetchImpl = globalThis.fetch } = {}) {
  let res
  try {
    res = await fetchImpl(`${url}/functions/v1/replay-register`, {
      method: 'POST',
      // the publishable key goes on apikey only: it isn't a JWT, so it never goes in Authorization
      headers: { 'content-type': 'application/json', apikey: key },
      body: JSON.stringify({ action, ...body }),
    })
  } catch {
    return { ok: false, error: 'failed' }
  }
  let answer = null
  try { answer = await res.json() } catch { answer = null }
  if (res.ok && answer && answer.ok === true) return answer
  if (answer && answer.ok === false && ERRORS.includes(answer.error)) return { ok: false, error: answer.error }
  return { ok: false, error: res.status === 429 ? 'busy' : 'failed' }
}

// ---- The pass, per slug, in this browser -----------------------------------------------------------

const PASS_SHAPE = /^[A-Za-z0-9_-]{16,256}$/
export const passKey = slug => `hub.replay-pass.${slug}`

// The local storage to use, or null where reading it throws (a private window, blocked site data)
export function localStore() {
  try { return globalThis.localStorage || null } catch { return null }
}

// The pass still in date for this slug, or null (and a stale or broken 1 is cleared)
export function readPass(store, slug, nowMs = Date.now()) {
  if (!store || !slug) return null
  let raw = null
  try { raw = store.getItem(passKey(slug)) } catch { return null }
  if (!raw) return null
  let kept = null
  try { kept = JSON.parse(raw) } catch { kept = null }
  const expires = Date.parse(kept?.expires_at || '')
  if (!kept || !PASS_SHAPE.test(String(kept.pass || '')) || !Number.isFinite(expires) || expires <= nowMs) {
    forgetPass(store, slug)
    return null
  }
  return kept.pass
}

export function savePass(store, slug, pass, expiresAt) {
  if (!store || !slug || !PASS_SHAPE.test(String(pass || ''))) return false
  try {
    store.setItem(passKey(slug), JSON.stringify({ pass, expires_at: expiresAt || null }))
    return true
  } catch {
    return false
  }
}

export function forgetPass(store, slug) {
  if (!store || !slug) return
  try { store.removeItem(passKey(slug)) } catch { /* storage off: nothing kept anyway */ }
}

// ---- What the page draws ---------------------------------------------------------------------------

// The webinar's date as the replay page writes it ("Wed 16 Sep") with the date block for the grey
// panel, from the function's date (the webinar's start, an ISO time). Anything else is shown as it came,
// with no block.
export function dateParts(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const ms = Date.parse(value)
    if (Number.isFinite(ms)) return { text: f.shortDate(ms), block: f.dateBlock(ms) }
  }
  return { text: typeof value === 'string' ? value : '', block: null }
}

// a link the page may open or load: https only, as admin's call to action check has it
const https = u => typeof u === 'string' && /^https:\/\/[^\s/]/i.test(u)

// page's answer as the form draws it
export function pageView(page) {
  const d = dateParts(page?.date)
  return {
    slug: page?.slug || null,
    title: String(page?.title || ''),
    date: d.text,
    block: d.block,
    thumbnailUrl: https(page?.thumbnail_url) ? page.thumbnail_url : null,
  }
}

function vimeoOf(v) {
  if (v && typeof v === 'object' && /^\d+$/.test(String(v.id || ''))) {
    return { id: String(v.id), hash: v.hash && /^[0-9a-z]+$/i.test(String(v.hash)) ? String(v.hash) : null }
  }
  // a link, should the function ever send 1
  return typeof v === 'string' ? parseVimeo(v) : null
}

function chaptersOf(list) {
  return (Array.isArray(list) ? list : [])
    .filter(c => Number.isFinite(c?.seconds) && c.title)
    .sort((a, b) => a.seconds - b.seconds)
    .map(c => ({ seconds: c.seconds, time: c.time || f.clock(c.seconds), title: String(c.title) }))
}

function ctaOf(cta) {
  if (!cta || !cta.label || !https(cta.url)) return null
  return {
    sellsName: cta.sells_name || null,
    sellsLook: cta.sells_look || null,
    text: cta.text || '',
    label: String(cta.label),
    url: cta.url,
  }
}

// replay's answer (and register's) as the watching page draws it, in WebinarReplay's shape
export function replayView(replay) {
  const d = dateParts(replay?.date)
  return {
    id: replay?.id || null,
    slug: replay?.slug || null,
    title: String(replay?.title || ''),
    date: d.text,
    block: d.block,
    description: replay?.description || '',
    vimeo: vimeoOf(replay?.vimeo),
    chapters: chaptersOf(replay?.chapters),
    thumbnailUrl: https(replay?.thumbnail_url) ? replay.thumbnail_url : null,
    sellsName: replay?.sells_name || null,
    sellsLook: replay?.sells_look || null,
    cta: ctaOf(replay?.cta),
  }
}

// ---- Admin's register settings (spec 3.1) ---------------------------------------------------------

// The name in MailerLite admin offers from the slug: '4-things-october' gives '4 things october',
// which Adam corrects to '4 things October'
export const listNameFromSlug = slug => String(slug || '').split('-').filter(Boolean).join(' ').slice(0, 60)

// The group every registration goes into (n8n's replay-event)
export const groupName = name => `Replay - ${String(name || '').trim()} - Registered`

// The list name's own rule: 1 to 60 characters once trimmed, no line breaks
export const isListName = name => {
  const n = String(name ?? '').trim()
  return n.length >= 1 && n.length <= 60 && !/[\r\n]/.test(n)
}
